import { Component, computed, effect, inject, OnDestroy, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import moment from 'moment-timezone';
import { SvgIconsComponent } from '../svg-icons/svg-icons.component';
import { ButtonComponentComponent } from '../button-component/button-component.component';
import { ToastrMessageComponent } from '../formComponent/toastr-message/toastr-message.component';
import { ReminderApis } from '../../../services/taskManagement/reminderApis';
import { UsersService } from '../../../constants/usersDetails';
import { MyRemindersService } from '../my-reminders/my-reminders.service';

const BAR_HEIGHT = '2.25rem';
const REFRESH_INTERVAL_MS = 60 * 1000;
const SNOOZE_PRESETS = [5, 15, 30, 60];
/** Reminders shown inline; the rest are one click away through "+N more" and the reminders panel. */
const MAX_INLINE = 3;

@Component({
  selector: 'app-reminders-marquee',
  standalone: true,
  imports: [SvgIconsComponent, ButtonComponentComponent],
  templateUrl: './reminders-marquee.component.html',
  styleUrl: './reminders-marquee.component.css',
})
export class RemindersMarqueeComponent implements OnDestroy {
  ui = inject(MyRemindersService);
  private reminderApis = inject(ReminderApis);
  private usersService = inject(UsersService);
  private toastr = inject(ToastrMessageComponent);
  private doc = inject(DOCUMENT);

  readonly snoozePresets = SNOOZE_PRESETS;
  readonly due = this.ui.due;
  readonly loading = this.ui.dueLoading;
  readonly capped = this.ui.dueCapped;
  readonly inline = computed(() => this.due().slice(0, MAX_INLINE));
  readonly overflowCount = computed(() => Math.max(0, this.due().length - MAX_INLINE));
  readonly countLabel = computed(() => (this.capped() ? `${this.due().length}+` : String(this.due().length)));
  readonly labelTitle = computed(() =>
    this.capped() ? `More than ${this.due().length} reminders are due — only the latest ${this.due().length} are shown. Dismiss or delete old ones to clear the clutter.` : 'Open my reminders'
  );
  readonly busyId = signal<number | null>(null);
  private pollTimer: any;
  private onWindowFocus = () => this.ui.loadDue();

  constructor() {
    this.ui.loadDue();
    effect(() => {
      this.doc.documentElement.style.setProperty('--app-top-bar', this.due().length ? BAR_HEIGHT : '0rem');
    });
    this.pollTimer = setInterval(() => this.ui.loadDue(), REFRESH_INTERVAL_MS);
    window.addEventListener('focus', this.onWindowFocus);
  }

  ngOnDestroy(): void {
    if (this.pollTimer) clearInterval(this.pollTimer);
    window.removeEventListener('focus', this.onWindowFocus);
    this.doc.documentElement.style.setProperty('--app-top-bar', '0rem');
  }

  timeLabel(r: any): string {
    const tz = this.usersService.USER?.timezone || 'UTC';
    const m = moment.utc(r?.remind_at, 'YYYY-MM-DD HH:mm:ss').tz(tz);
    if (!m.isValid()) return '';
    const now = moment().tz(tz);
    if (m.isSame(now, 'day')) return m.format('h:mm a');
    return m.format(m.isSame(now, 'year') ? 'MMM D, h:mm a' : 'MMM D YYYY, h:mm a');
  }

  snoozeLabel(minutes: number): string {
    return minutes < 60 ? `${minutes}m` : '1h';
  }

  snoozeTitle(minutes: number): string {
    return minutes < 60 ? `Snooze ${minutes} min` : 'Snooze 1 hour';
  }

  refresh(): void {
    this.ui.loadDue();
  }

  openAll(): void {
    this.ui.open.set(true);
  }

  open(r: any): void {
    this.ui.openWith(r.id);
  }

  dismiss(r: any): void {
    if (this.busyId()) return;
    this.busyId.set(r.id);
    this.reminderApis.dismissReminder(r.id).subscribe({
      next: () => {
        this.busyId.set(null);
        this.toastr.showSuccess('Reminder dismissed');
        this.ui.notifyChanged();
      },
      error: (err: any) => {
        this.busyId.set(null);
        this.toastr.showError(err?.message || 'Failed to dismiss reminder');
      },
    });
  }

  snooze(r: any, minutes: number): void {
    if (this.busyId()) return;
    const until = moment.utc().add(minutes, 'minutes').format('YYYY-MM-DD HH:mm');
    this.busyId.set(r.id);
    this.reminderApis.snoozeReminder(r.id, until).subscribe({
      next: () => {
        this.busyId.set(null);
        this.toastr.showSuccess(`Snoozed for ${minutes < 60 ? minutes + ' minutes' : '1 hour'}`);
        this.ui.notifyChanged();
      },
      error: (err: any) => {
        this.busyId.set(null);
        this.toastr.showError(err?.message || 'Failed to snooze reminder');
      },
    });
  }
}
